import React, { memo, useEffect, useRef, useState } from 'react';

import { avatarFrameRender, avatarGlowRender } from './styleRenderer';

import './avatar-select.css';
import { useTranslations } from 'next-intl';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import {
  useGetCurrentUser,
  useInvalidateCurrentUser,
} from '@wwe-portal/web/providers/queries/user-info';
import {
  useGetAllAvatars,
  useGetAllFrames,
} from '@wwe-portal/web/providers/queries/webcoin';
import { Button, ClickableDiv } from '@wwe-portal/ui/components/button';
import MarketItemsRarityDropdown from '../../flash-market/market-items-rarity-dropdown';
import { cn } from '@wwe-portal/ui/lib/lib';
import {
  useUpdatePlayerAvatar,
  useUpdatePlayerFrame,
  useRemovePlayerFrame,
} from '@wwe-portal/web/providers/queries/user.mutation';
import { UserAvatar, UserFrame } from '@wwe-portal/entities/webcoin.type';
import { RarityProps } from '../../flash-market/market-items-popup/market-items-popup';
import LoadAnimateButton from '../../load-animate-button';

const rarity = [
  {
    id: 1,
    key: 'All',
    name: 'All',
    color: '',
  },
  {
    id: 2,
    key: 'Common',
    name: 'Common',
    color: '',
  },
  {
    id: 3,
    key: 'Rare',
    name: 'Rare',
    color: '',
  },
  {
    id: 4,
    key: 'Epic',
    name: 'Epic',
    color: '',
  },
  {
    id: 5,
    key: 'Legendary',
    name: 'Legendary',
    color: '',
  },
] as RarityProps[];

function AvatarSelect({ onClose }) {
  /**
   * Set Modal Show
   */
  /**
   * Set chosen Avatar
   */
  const [currentAvatar, setCurrentAvatar] = useState<UserAvatar>();
  /**
   * Set chosen Frame
   */
  const [currentFrame, setCurrentFrame] = useState<UserFrame>();
  /**
   *  Set Apply processing
   */
  const [applyProcessing, setApplyProcessing] = useState(false);
  /**
   *  Available Avatars
   */
  const [desktopAvatars, setDesktopAvatars] = useState<UserAvatar[]>([]);
  /**
   *  Locked Avatars
   */
  const [lockedAvatars, setLockedAvatars] = useState<UserAvatar[]>([]);
  /**
   *  Mobile Avatars
   */
  const [mobileAvatars, setMobileAvatars] = useState<UserAvatar[]>([]);
  /**
   *  Mobile Frames
   */
  const [mobileFrames, setMobileFrames] = useState<UserFrame[]>([]);
  /**
   *  Available Avatars
   */
  const [desktopFrames, setDesktopFrames] = useState<UserFrame[]>([]);
  /**
   *  Locked Avatars
   */
  const [lockedFrames, setLockedFrames] = useState<UserFrame[]>([]);
  /**
   *  Avatar or Frame
   */
  const [avatar, setAvatar] = useState(true);
  /**
   *  Rarity change
   */

  const [active, setActive] = useState({
    name: 'Filter by Rarity',
    id: 0,
    color: '',
    key: 'Filter by Rarity',
  });

  /**
   *  Rarity change
   */
  const [removeFrame, setRemoveFrame] = useState(false);

  /**
   * Use Dispatch hook
   * Using react-redux
   */
  /**
   * Use translation hook
   * @see https://react.i18next.com/
   */
  const t = useTranslations();

  /**
   * Use window size custom hook
   */
  const { isMobile } = useResponsive();

  const currentUser = useGetCurrentUser();

  const onSuccessUpdate = () => {
    invalidateCurrentUser();
    setApplyProcessing(false);
    handleClose();
  };

  const invalidateCurrentUser = useInvalidateCurrentUser();
  const { mutate: updatePlayerAvatar } = useUpdatePlayerAvatar({
    onSuccess: onSuccessUpdate,
  });
  const { mutate: updatePlayerFrame } = useUpdatePlayerFrame({
    onSuccess: onSuccessUpdate,
  });
  const { mutate: callRemoveFrame } = useRemovePlayerFrame({
    onSuccess: () => {
      setApplyProcessing(false);
      handleClose();
      invalidateCurrentUser();
    },
  });
  const { data: avatarsData, isLoading: isAvatarsLoading } = useGetAllAvatars();
  const { data: framesData, isLoading: isFramesLoading } = useGetAllFrames();

  const isLoading = isAvatarsLoading && isFramesLoading;
  const avatars = avatarsData?.data.data;
  const frames = framesData?.data.data;

  /**
   * Avatar Frame Icons Block sections ref
   */
  const avatarFrameIconsBlock = useRef<HTMLDivElement>(null);
  /**
   * Avatar Frame Icons Block Mobile sections ref
   */
  const avatarFrameIconsBlockMob = useRef<HTMLDivElement>(null);
  /**
   * On Modal close handler
   */

  const currentChoosenFrameRef = useRef<UserFrame>(null);

  const handleClose = () => {
    onClose();
  };

  /**
   * Disable Apply when a locked avatar or frame is chosen
   */
  const isLockedSelection =
    currentAvatar?.unlocked === false || currentFrame?.unlocked === false;

  const handleRemoveFrame = () => {
    if (removeFrame) {
      setApplyProcessing(true);
      callRemoveFrame();
    }
  };

  /**
   * On chosen avatar apply handler
   */
  const handlePlayerAvatarApply = () => {
    handleRemoveFrame();

    setApplyProcessing(true);

    if (currentAvatar && typeof currentAvatar.avatarId !== 'undefined') {
      updatePlayerAvatar({ avatarId: currentAvatar.avatarId });
    }
    if (currentFrame && typeof currentFrame.frameId !== 'undefined') {
      updatePlayerFrame({ frameId: currentFrame.frameId });
    }
  };

  const sortingByRarity = ({
    list,
    unlocked,
  }: {
    list: UserAvatar[] | UserFrame[];
    unlocked: boolean;
  }) => {
    return list
      .filter((avatar: UserAvatar | UserFrame) => avatar.unlocked === unlocked)
      .map((item) => {
        const newItem = { ...item, sortNumber: 0 };

        switch (newItem.rarity) {
          case 'Legendary':
            newItem.sortNumber = 4;
            break;
          case 'Epic':
            newItem.sortNumber = 3;
            break;
          case 'Rare':
            newItem.sortNumber = 2;
            break;
          default:
            newItem.sortNumber = 1;
            break;
        }
        return newItem;
      })
      .sort((a, b) => a.sortNumber - b.sortNumber) as any;
  };

  useEffect(() => {
    if (avatars && avatars.length > 0 && desktopAvatars.length < 1) {
      setDesktopAvatars(sortingByRarity({ list: avatars, unlocked: true }));
      setLockedAvatars(sortingByRarity({ list: avatars, unlocked: false }));
      setMobileAvatars([
        ...sortingByRarity({ list: avatars, unlocked: true }),
        ...sortingByRarity({ list: avatars, unlocked: false }),
      ]);
    }

    if (frames && frames.length > 0 && desktopFrames.length < 1) {
      setDesktopFrames(sortingByRarity({ list: frames, unlocked: true }));
      setLockedFrames(sortingByRarity({ list: frames, unlocked: false }));
      setMobileFrames([
        ...sortingByRarity({ list: frames, unlocked: true }),
        ...sortingByRarity({ list: frames, unlocked: false }),
      ]);
    }
    // eslint-disable-next-line
  }, [avatars, frames]);

  /**
   * Avatar and Frame Filter
   * @param item
   */
  const selectFilter = (item) => {
    setActive(item);
    setScrollToTop();
    if (avatars && frames) {
      if (item.key === 'All') {
        setDesktopAvatars(sortingByRarity({ list: avatars, unlocked: true }));
        setDesktopFrames(sortingByRarity({ list: frames, unlocked: true }));
        setLockedAvatars(sortingByRarity({ list: avatars, unlocked: false }));
        setLockedFrames(sortingByRarity({ list: frames, unlocked: false }));
        setMobileAvatars([
          ...sortingByRarity({ list: avatars, unlocked: true }),
          ...sortingByRarity({ list: avatars, unlocked: false }),
        ]);
        setMobileFrames([
          ...sortingByRarity({ list: frames, unlocked: true }),
          ...sortingByRarity({ list: frames, unlocked: false }),
        ]);
      } else {
        setDesktopAvatars(
          avatars
            .filter((avatar) => avatar.rarity === item.key)
            .filter((item) => item.unlocked === true)
        );
        setLockedAvatars(
          avatars
            .filter((avatar) => avatar.rarity === item.key)
            .filter((item) => item.unlocked === false)
        );
        setDesktopFrames(
          frames
            .filter((frame) => frame.rarity === item.key)
            .filter((item) => item.unlocked === true)
        );
        setLockedFrames(
          frames
            .filter((frame) => frame.rarity === item.key)
            .filter((item) => item.unlocked === false)
        );
        setMobileAvatars(
          avatars.filter((avatar) => avatar.rarity === item.key)
        );
        setMobileFrames(frames.filter((frame) => frame.rarity === item.key));
      }
    }
  };
  /**
   * Avatar and Frame Filter
   * Automatically scroll up
   */
  const setScrollToTop = () => {
    if (isMobile) {
      const element = avatarFrameIconsBlockMob.current;
      element?.scrollTo(0, 0);
    } else {
      const element = avatarFrameIconsBlock.current;
      element?.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    if (currentUser) {
      setCurrentFrame({
        ...(currentFrame as UserFrame),
        link: currentUser.profileFrame,
      });
    }
    currentChoosenFrameRef.current = {
      ...(currentFrame as UserFrame),
      link: currentUser?.profileFrame ?? '',
    };
    // eslint-disable-next-line
  }, [currentUser]);

  return (
    <div className="avatar-select-modal">
      {isLoading ? (
        <></>
      ) : (
        <div
          className={`avatar-select-block bg-back200 relative flex items-center justify-between ${
            isMobile && 'flex-col'
          }`}
        >
          {currentUser && (
            <div className="avatar-select-block-side flex flex-col items-center">
              <div className="avatar-select-background">
                <div
                  className="avatar-select-background-image rounded-full"
                  style={{
                    backgroundImage: `url(${currentAvatar?.link || currentUser?.profileImg || '/assets/images/common/the-rock.webp'})`,
                  }}
                >
                  <div
                    className="avatar-select-glow avatar-select-glow-card p-30 avatar-select-glow-width"
                    style={avatarGlowRender(
                      (!currentFrame || (currentFrame && !currentFrame.link)) &&
                        !currentUser.profileFrame
                        ? currentAvatar
                          ? currentAvatar.rarity
                          : currentUser.avatarRarity
                        : removeFrame
                          ? currentAvatar
                            ? currentAvatar.rarity
                            : currentUser.avatarRarity
                          : null
                    )}
                  >
                    <div
                      className="avatar-select-glow avatar-select-glow-card avatar-select-glow-width p-[30px]"
                      style={avatarGlowRender(active.name)}
                    >
                      <div
                        className="avatar-select-background-border"
                        style={{
                          backgroundImage: `url(${
                            currentFrame && !removeFrame
                              ? currentFrame.link
                              : null
                          })`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {!isMobile && (
                <div className="flex w-full flex-col items-center">
                  <Button
                    onClick={handlePlayerAvatarApply}
                    disabled={isLockedSelection}
                    variant="blue"
                    className="h5 h-13 mb-0 mt-4 w-[80%] text-base"
                    data-id="avatar_change_apply_button"
                  >
                    {applyProcessing ? (
                      <LoadAnimateButton bodySize="text-4" />
                    ) : (
                      t('apply')
                    )}
                  </Button>

                  <p
                    className={`text-text400 mb-0 mt-[10px] cursor-pointer text-sm font-medium ${
                      currentUser.profileFrame && !removeFrame
                        ? 'remove-frame'
                        : 'cursor-default'
                    }`}
                    onClick={() => {
                      if (currentUser.profileFrame && !removeFrame) {
                        setRemoveFrame(true);
                        setCurrentFrame({
                          ...(currentFrame as UserFrame),
                          frameId: undefined,
                        });
                      }
                    }}
                    data-id="avatar_select_remove_frame"
                  >
                    <ins>{t('remove_frame')}</ins>
                  </p>
                </div>
              )}
            </div>
          )}

          {isMobile && (
            <div className="avatar-select-right-header flex justify-between border-b-[#343961] pb-[15px] pt-5">
              <div className="flex w-full">
                <Button
                  variant="secondary"
                  className={cn(
                    'avatar-select-button border-borderColor9 text-chetwodeBlue mr-[10px] border-[1px] border-solid',
                    {
                      active: avatar,
                    }
                  )}
                  onClick={() => {
                    setAvatar(true);
                    setScrollToTop();
                  }}
                  data-id="avatar_select_show_avatars"
                >
                  {t('avatar_select')}
                </Button>
                <div className="flex-grow">
                  <Button
                    variant="secondary"
                    className={cn(
                      'avatar-select-button border-borderColor9 text-chetwodeBlue border-[1px] border-solid',
                      {
                        active: !avatar,
                      }
                    )}
                    onClick={() => {
                      setAvatar(false);
                      setScrollToTop();
                    }}
                    data-id="avatar_select_show_frames"
                  >
                    {t('frame')}
                  </Button>
                </div>
                <MarketItemsRarityDropdown
                  className="border-borderColor9 bg-bgColor15 text-chetwodeBlue h-[30px] w-[30px] rounded border-[1px] border-solid"
                  selectFilter={selectFilter}
                  rarities={rarity}
                  active={active}
                />
              </div>
            </div>
          )}
          <div
            className={cn(
              isLoading && 'overflow-hidden',
              'avatar-select-right'
            )}
          >
            {!isMobile ? (
              <div className="avatar-select-icons scrollbar-moz flex justify-evenly">
                <div className="avatar-select-right-header flex w-full justify-between border-b-[#343961] pt-5">
                  <div className="flex">
                    <Button
                      variant="secondary"
                      className={`avatar-select-button ${avatar ? 'active' : ''}`}
                      onClick={() => {
                        setAvatar(true);
                        setScrollToTop();
                      }}
                      data-id="avatar_select_show_avatars"
                    >
                      {t('avatar_select')}
                    </Button>
                    <Button
                      variant="secondary"
                      className={`avatar-select-button ml-[10px] ${avatar ? '' : 'active'}`}
                      onClick={() => {
                        setAvatar(false);
                        setScrollToTop();
                      }}
                      data-id="avatar_select_show_frames"
                    >
                      {t('frame')}
                    </Button>
                  </div>
                  <MarketItemsRarityDropdown
                    className="mr-10 h-[30px]"
                    selectFilter={selectFilter}
                    rarities={rarity}
                    active={active}
                  />
                </div>
                <div
                  ref={avatarFrameIconsBlock}
                  className="avatar-select-icons-block scrollbar-moz"
                >
                  {avatar ? (
                    <div className="">
                      {!!desktopAvatars.length && (
                        <>
                          <div className="avatar-select-right-item border-b-[#343961] pb-[10px] pt-5">
                            {t('available_avatars')}
                          </div>
                          <div className="avatar-select-right-icons mr-0 mt-0 flex flex-wrap justify-start overflow-hidden p-0 pb-[10px] pl-[5px]">
                            {desktopAvatars.map((avatar) => (
                              <ClickableDiv
                                onClick={() => {
                                  setCurrentAvatar(avatar);
                                  window.avatarId = `select_${avatar?.avatarId}_avatar`;
                                }}
                                key={avatar.avatarId}
                                className={`avatar-select-back ${
                                  currentAvatar &&
                                  currentAvatar.avatarId === avatar.avatarId
                                    ? 'active'
                                    : ''
                                }`}
                                data-id="select_avatar"
                              >
                                <div
                                  className="avatar-select-glow"
                                  style={avatarFrameRender(avatar.rarity)}
                                >
                                  <div
                                    className="avatar-select-image"
                                    style={{
                                      backgroundImage: `url(${avatar.link})`,
                                    }}
                                  />
                                </div>
                              </ClickableDiv>
                            ))}
                          </div>
                        </>
                      )}
                      {!!lockedAvatars.length && (
                        <>
                          <div className="avatar-select-right-item w-full border-b-[#343961] pb-[10px] pt-5">
                            {t('locked_avatars')}
                          </div>
                          <div className="avatar-select-right-icons mr-0 mt-0 flex flex-wrap justify-start overflow-hidden p-0 pb-[10px] pl-[5px]">
                            {lockedAvatars.map((avatar) => (
                              <div
                                key={avatar.avatarId}
                                className={`avatar-select-back`}
                                data-id="locked_avatar"
                              >
                                <div
                                  className="avatar-select-glow"
                                  style={avatarFrameRender(avatar.rarity)}
                                >
                                  <div
                                    className={`${!avatar.unlocked && 'lock'} avatar-select-image`}
                                    style={{
                                      backgroundImage: `url(${avatar.link})`,
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="">
                      {!!desktopFrames.length && (
                        <>
                          <div className="avatar-select-right-item border-b-[#343961] pb-[10px] pt-5">
                            {t('available_frames')}
                          </div>
                          <div
                            id="frames-list"
                            className="avatar-select-right-icons mr-0 mt-0 flex flex-wrap justify-start overflow-hidden p-0 pb-[10px] pl-[5px]"
                          >
                            {desktopFrames.map((frame) => (
                              <ClickableDiv
                                onClick={() => {
                                  if (currentFrame?.frameId === frame.frameId) {
                                    const currentChoosenFrame =
                                      currentChoosenFrameRef.current;
                                    setCurrentFrame(
                                      currentChoosenFrame ?? undefined
                                    );
                                    return;
                                  }

                                  setCurrentFrame(frame);
                                  setRemoveFrame(false);
                                  window.frameDataId = `select_${frame?.frameId}_frame`;
                                }}
                                key={frame.frameId}
                                className={`avatar-select-back ${currentFrame?.frameId === frame.frameId ? 'active' : ''}`}
                                data-id="select_frame"
                              >
                                <div className="avatar-select-glow">
                                  <div
                                    className="avatar-select-image-frame"
                                    style={{
                                      backgroundImage: `url(${frame.link})`,
                                    }}
                                  />
                                </div>
                              </ClickableDiv>
                            ))}
                          </div>
                        </>
                      )}
                      {!!lockedFrames.length && (
                        <>
                          <div className="avatar-select-right-item w-full border-b-[#343961] pb-[10px] pt-5">
                            {t('locked_frames')}
                          </div>
                          <div className="avatar-select-right-icons mr-0 mt-0 flex flex-wrap justify-start overflow-hidden p-0 pb-[10px] pl-[5px]">
                            {lockedFrames.map((frame) => (
                              <div
                                id="frames-list"
                                key={frame.frameId}
                                className="avatar-select-back"
                                data-id="locked_frame"
                              >
                                <div className="avatar-select-glow">
                                  <div
                                    className={`${
                                      !frame.unlocked && 'lock'
                                    } avatar-select-image-frame`}
                                    style={{
                                      backgroundImage: `url(${frame.link})`,
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                ref={avatarFrameIconsBlockMob}
                className={`avatar-select-icons-block scrollbar-moz mr-0 flex justify-evenly rounded-xl bg-[#3d426e] ${
                  !isMobile ? 'px-[5px] py-[10px]' : 'p-0'
                }`}
              >
                {avatar ? (
                  <div>
                    <div className="mr-0 flex justify-evenly p-[10px]">
                      {mobileAvatars.map((avatar) => (
                        <ClickableDiv
                          onClick={() => {
                            if (avatar.avatarId === currentAvatar?.avatarId) {
                              setCurrentAvatar(undefined);
                              return;
                            }
                            setCurrentAvatar(avatar);
                            window.avatarId = `select_${avatar?.avatarId}_avatar`;
                          }}
                          key={avatar.avatarId}
                          className={`avatar-select-back ${currentAvatar && currentAvatar.avatarId === avatar.avatarId ? 'active' : ''}`}
                          data-id="select_avatar"
                        >
                          <div
                            className="avatar-select-glow"
                            style={avatarFrameRender(avatar.rarity)}
                          >
                            <div
                              className={`${
                                !avatar.unlocked && 'lock avatar-lock'
                              } avatar-select-image`}
                              style={{
                                backgroundImage: `url(${avatar.link})`,
                              }}
                            />
                          </div>
                        </ClickableDiv>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mr-0 flex justify-evenly p-[10px]">
                      {mobileFrames?.map((frame) => (
                        <ClickableDiv
                          onClick={() => {
                            if (currentFrame?.frameId === frame.frameId) {
                              const currentChoosenFrame =
                                currentChoosenFrameRef.current;
                              setCurrentFrame(currentChoosenFrame ?? undefined);
                              return;
                            }
                            setCurrentFrame(frame);
                            window.frameDataId = `select_${frame?.frameId}_frame`;
                            setRemoveFrame(false);
                          }}
                          key={frame.frameId}
                          className={`avatar-select-back ${currentFrame && currentFrame.frameId === frame.frameId ? 'active' : ''}`}
                          data-id="select_frame"
                        >
                          <div className="avatar-select-glow">
                            <div
                              className={`${!frame.unlocked && 'lock'} avatar-select-image`}
                              style={{ backgroundImage: `url(${frame.link})` }}
                            />
                          </div>
                        </ClickableDiv>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="right-shadow" />
          </div>
          {isMobile ? (
            <div className="flex w-full flex-col items-center">
              <Button
                variant="blue"
                onClick={handlePlayerAvatarApply}
                disabled={isLockedSelection}
                className="h5 h-13 mb-0 mt-4 w-[80%] text-base"
                data-id="avatar_select_modal_apply_changes"
              >
                {applyProcessing ? (
                  <LoadAnimateButton bodySize="text-4" />
                ) : (
                  t('apply')
                )}
              </Button>
              <p
                className="remove-frame text-text400 mb-0 mt-[10px] cursor-pointer text-sm font-medium"
                onClick={() => setRemoveFrame(true)}
                data-id="avatar_select_modal_remove_frame"
              >
                <ins>{t('remove_frame')}</ins>
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default memo(AvatarSelect);
